import { ApiError } from './client';
import { ERROR_CODE } from './errors';

/** A refusal split over a form: the sentences that belong to one of its fields, and what is left for the form. */
export interface ServerFieldErrors<K extends string> {
  fields: Partial<Record<K, string>>;
  /** the message for the form itself (null when every detail found its field) */
  form: string | null;
}

/**
 * Spread a refusal over a form's fields:
 * - a validation failure: each detail names its API field (`name`, `items[1].epc`); its top-level name picks the
 *   form field in `fields`. The sentence starts with the API name ("name wajib diisi"), so that word is shown as
 *   the form's label ("Nama wajib diisi");
 * - a specific code the form knows (`byCode`, e.g. ALREADY_EXISTS -> the email field): the message goes there.
 * Anything else stays a form-level message.
 */
export function serverFieldErrors<K extends string>(e: unknown, fields: Partial<Record<string, readonly [K, string]>>, byCode: Partial<Record<string, K>> = {}): ServerFieldErrors<K> {
  if (!(e instanceof ApiError)) return { fields: {}, form: null };
  const out: Partial<Record<K, string>> = {};
  const coded = byCode[e.code];
  if (coded) return { fields: { [coded]: e.message } as Partial<Record<K, string>>, form: null };
  if (e.code !== ERROR_CODE.VALIDATION_FAILED || !e.details.length) return { fields: out, form: e.message };
  const rest: string[] = [];
  for (const d of e.details) {
    const target = d.field ? fields[d.field.split(/[.[]/)[0]] : undefined;
    if (!target) rest.push(d.message);
    else if (!out[target[0]]) out[target[0]] = d.field && d.message.startsWith(`${d.field} `) ? `${target[1]}${d.message.slice(d.field.length)}` : d.message;
  }
  return { fields: out, form: rest.length ? `${e.message}: ${rest.join('; ')}` : null };
}
